let _jsZipLoaded = false;
let _dexieLoaded = false;

async function ensureDexie() {
    if (!_dexieLoaded) {
        await import('./Lib/Dexie/dexie.min.js');
        _dexieLoaded = true;
    }
}

async function ensureJSZip() {
    if (!_jsZipLoaded) {
        await import('./Lib/JSZip/jszip.min.js');
        _jsZipLoaded = true;
    }
}

/**
 * Assembles chunks from IndexedDB into a complete file, handles ZIP extraction,
 * and stores results back to IndexedDB — all in JavaScript to avoid OOM on the
 * .NET WASM managed heap.
 *
 * @param {string} dbName - Dexie database name (e.g. "UP")
 * @param {string} chunksTable - Chunks store name (e.g. "Chunks")
 * @param {string} assetsTable - Assets metadata store name (e.g. "Assets")
 * @param {string} contentTable - Assets content store name (e.g. "AssetContent")
 * @param {string} chunkIndex - Index name for chunk lookup (e.g. "chunkIdentifier")
 * @param {string} chunkIdentifier - The asset ID used to group chunks
 * @param {string} assetId - Primary key for the composed asset
 * @param {string} filename - Original filename from the download
 * @param {string|null} ownerId - Parent owner ID (null for top-level assets)
 * @param {string} userId - User GUID string
 * @param {string|null} lastModified - ISO date string or null
 * @param {string} assetIdBase - Base string for computing child asset IDs
 * @returns {Promise<{totalSize: number, isZip: boolean, extractedFiles: Array<{assetId: string, filename: string, size: number}>}>}
 */
export async function assembleAndStoreAsset(
    dbName, chunksTable, assetsTable, contentTable, chunkIndex,
    chunkIdentifier, assetId, filename, ownerId, userId, lastModified, assetIdBase) {

    const db = await openDb(dbName);

    const chunksStore = db.table(chunksTable);
    const assetsStore = db.table(assetsTable);
    const contentStore = db.table(contentTable);

    // 1. Read all chunks for this identifier, sorted by offset
    const chunks = await chunksStore
        .where(chunkIndex)
        .equals(chunkIdentifier)
        .sortBy('offset');

    if (!chunks || chunks.length === 0) {
        return {totalSize: 0, isZip: false, extractedFiles: []};
    }

    // 2. Calculate total size from chunk metadata (not content length, since
    //    content may contain extra bytes from ArrayPool)
    let totalSize = 0;
    for (const chunk of chunks) {
        totalSize += chunk.size;
    }

    // 2b. Validate assembled size matches expected total from download metadata
    const expectedTotal = chunks[0].totalSize;
    if (expectedTotal > 0 && totalSize !== expectedTotal) {
        const chunkIds = chunks.map(c => c.id);
        await chunksStore.bulkDelete(chunkIds);
        throw new Error(
            `Chunk integrity check failed for ${chunkIdentifier}: ` +
            `expected ${expectedTotal} bytes but chunks total ${totalSize} bytes`);
    }

    // 3. Build array of correctly-sized Uint8Array slices (no full-file concatenation)
    const chunkSlices = [];
    for (const chunk of chunks) {
        const decoded = getContentAsUint8Array(chunk);
        // .slice() copies only chunk.size bytes into a correctly-sized buffer
        // (decoded may be backed by a larger ArrayPool buffer)
        chunkSlices.push(decoded.slice(0, chunk.size));
    }

    // 4. Check ZIP magic bytes (PK\x03\x04) from first slice
    const first = chunkSlices[0];
    const isZip = totalSize >= 4 &&
        first[0] === 0x50 && first[1] === 0x4B &&
        first[2] === 0x03 && first[3] === 0x04;

    const extractedFiles = [];

    if (isZip) {
        // 5a. Build a Blob from slices (browser manages storage efficiently,
        //     potentially disk-backed) and pass to JSZip which accepts Blob natively
        const assembledBlob = new Blob(chunkSlices);
        chunkSlices.length = 0; // Release slice references for GC
        await ensureJSZip();
        const zip = await JSZip.loadAsync(assembledBlob);

        const metadataRecords = [];
        const contentRecords = [];

        for (const [entryName, zipEntry] of Object.entries(zip.files)) {
            // Skip directories and macOS resource fork files
            if (zipEntry.dir) continue;
            if (entryName.startsWith('__MACOSX/')) continue;
            if (entryName.includes('/._')) continue;

            const entryData = await zipEntry.async('uint8array');
            const childAssetId = utf8ToBase64(assetIdBase + ':' + zipEntry.name);

            metadataRecords.push({
                id: childAssetId,
                size: entryData.length,
                lastModified: lastModified,
                filename: entryName,
                ownerId: assetId, // parent ZIP's assetId
                userId: userId
            });
            contentRecords.push({id: childAssetId, content: entryData});

            extractedFiles.push({
                assetId: childAssetId,
                filename: entryName,
                size: entryData.length
            });
        }

        // Store empty parent record for the ZIP itself
        metadataRecords.push({
            id: assetId,
            size: 0,
            lastModified: lastModified,
            filename: filename,
            ownerId: ownerId,
            userId: userId
        });
        contentRecords.push({id: assetId, content: new Uint8Array(0)});

        // Batch write metadata and content in a single IndexedDB transaction
        await db.transaction('rw', assetsStore, contentStore, async () => {
            await assetsStore.bulkPut(metadataRecords);
            await contentStore.bulkPut(contentRecords);
        });
    } else {
        // 5b. Concatenate slices into Uint8Array for IndexedDB storage
        const assembled = new Uint8Array(totalSize);
        let writeOffset = 0;
        for (const slice of chunkSlices) {
            assembled.set(slice, writeOffset);
            writeOffset += slice.length;
        }
        chunkSlices.length = 0; // Release slice references for GC

        await db.transaction('rw', assetsStore, contentStore, async () => {
            await assetsStore.put({
                id: assetId,
                size: totalSize,
                lastModified: lastModified,
                filename: filename,
                ownerId: ownerId,
                userId: userId
            });
            await contentStore.put({id: assetId, content: assembled});
        });
    }

    // 6. Delete all chunk records
    const chunkIds = chunks.map(c => c.id);
    await chunksStore.bulkDelete(chunkIds);

    return {totalSize, isZip, extractedFiles};
}

/**
 * Retrieves raw asset content as Uint8Array from IndexedDB.
 * Used by XmlTransformerInterop to load SEF directly without base64 round-trip.
 */
export async function getAssetRecord(dbName, table, assetId) {
    const db = await openDb(dbName);
    const record = await db.table('AssetContent').get(assetId);
    if (!record) return null;

    return getContentAsUint8Array(record);
}

/**
 * Opens a Dexie database. Reuses the instance created by IndexedDbManager.
 * The instance is registered synchronously in createDb before db.open(),
 * so it is always available by the time C# calls into this module.
 */
async function openDb(dbName) {
    await ensureDexie();

    if (window.__dexieInstances && window.__dexieInstances[dbName]) {
        return window.__dexieInstances[dbName];
    }

    throw new Error(`Database "${dbName}" not found. ` +
        `Ensure BlazorDbFactory.GetDbManager() is called first.`);
}

/**
 * Extracts content as Uint8Array from a record, handling both legacy base64
 * strings and new Uint8Array storage.
 */
function getContentAsUint8Array(record) {
    if (record.content instanceof Uint8Array) return record.content;
    if (record.content instanceof ArrayBuffer) return new Uint8Array(record.content);
    if (typeof record.content === 'string') {
        if (record.content.length === 0) return new Uint8Array(0);
        return base64ToUint8Array(record.content);
    }
    return null;
}

/**
 * Revokes a single blob URL to release the underlying memory.
 */
export function revokeBlobUrl(url) {
    if (url) URL.revokeObjectURL(url);
}

/**
 * Revokes multiple blob URLs to release the underlying memory.
 */
export function revokeBlobUrls(urls) {
    if (urls) urls.forEach(url => URL.revokeObjectURL(url));
}

/**
 * Creates a blob URL from an asset stored in IndexedDB.
 * Used by binary consumers (BabylonCanvas, DocEmbed, IetpViewer images/video)
 * to display content without passing through C#.
 */
export async function createBlobUrl(dbName, table, assetId, mimeType) {
    const db = await openDb(dbName);
    const record = await db.table('AssetContent').get(assetId);
    if (!record) return null;

    let bytes = getContentAsUint8Array(record);
    if (!bytes) return null;

    // Release IndexedDB deserialization buffer for GC
    record.content = null;

    // Use raw ArrayBuffer when possible — V8 optimizes ArrayBuffer-to-Blob
    // transfer better than typed array copies
    let blob;
    if (bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength) {
        blob = new Blob([bytes.buffer], {type: mimeType});
    } else {
        blob = new Blob([bytes], {type: mimeType});
    }

    bytes = null;
    return URL.createObjectURL(blob);
}

/**
 * Retrieves asset content as a base64 string for text consumers.
 * C# callers decode the base64 to get byte[].
 */
export async function getAssetContentBase64(dbName, table, assetId) {
    const db = await openDb(dbName);
    const record = await db.table('AssetContent').get(assetId);
    if (!record) return null;

    const bytes = getContentAsUint8Array(record);
    if (!bytes) return null;

    return uint8ArrayToBase64(bytes);
}

/**
 * Retrieves asset metadata without loading content.
 * Returns { id, size, lastModified, filename, ownerId, userId } or null.
 */
export async function getAssetMetadata(dbName, table, assetId) {
    const db = await openDb(dbName);
    const store = db.table(table);
    const record = await store.get(assetId);
    if (!record) return null;

    return {
        id: record.id,
        size: record.size,
        lastModified: record.lastModified,
        filename: record.filename,
        ownerId: record.ownerId,
        userId: record.userId
    };
}

/**
 * Retrieves metadata for all assets in a table (no content loaded).
 */
export async function getAllAssetMetadata(dbName, table) {
    const db = await openDb(dbName);
    const store = db.table(table);

    const records = await store.toArray();
    return records.map(record => ({
        id: record.id,
        size: record.size,
        lastModified: record.lastModified,
        filename: record.filename,
        ownerId: record.ownerId,
        userId: record.userId
    }));
}

/**
 * Retrieves metadata for assets matching an index value (no content loaded).
 * Used for OwnerId lookups and similar queries.
 */
export async function getAssetMetadataByIndex(dbName, table, indexName, value) {
    const db = await openDb(dbName);
    const store = db.table(table);

    const records = await store.where(indexName).equals(value).toArray();
    return records.map(record => ({
        id: record.id,
        size: record.size,
        lastModified: record.lastModified,
        filename: record.filename,
        ownerId: record.ownerId,
        userId: record.userId
    }));
}

/**
 * Decodes a base64 string to Uint8Array.
 */
function base64ToUint8Array(base64) {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

/**
 * Encodes a Uint8Array to base64 string.
 */
function uint8ArrayToBase64(bytes) {
    const chunks = [];
    const len = bytes.byteLength;
    // Process in chunks to avoid call stack overflow with large arrays
    const chunkSize = 8192;
    for (let i = 0; i < len; i += chunkSize) {
        const slice = bytes.subarray(i, Math.min(i + chunkSize, len));
        chunks.push(String.fromCharCode.apply(null, slice));
    }
    return btoa(chunks.join(''));
}

/**
 * Converts a UTF-8 string to base64 (matching C#'s Convert.ToBase64String(Encoding.UTF8.GetBytes(...))).
 */
function utf8ToBase64(str) {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(str);
    return uint8ArrayToBase64(bytes);
}

/**
 * Deletes an asset and all its children (ZIP extracts) in a single IndexedDB
 * transaction to prevent orphaned records on partial failure.
 *
 * @returns {Promise<string[]>} IDs of deleted child assets
 */
export async function deleteAssetWithChildren(dbName, assetsTable, contentTable, ownerIndex, assetId) {
    const db = await openDb(dbName);
    const assetsStore = db.table(assetsTable);
    const contentStore = db.table(contentTable);

    const children = await assetsStore.where(ownerIndex).equals(assetId).toArray();
    const childIds = children.map(c => c.id);

    await db.transaction('rw', assetsStore, contentStore, async () => {
        if (childIds.length > 0) {
            await assetsStore.bulkDelete(childIds);
            await contentStore.bulkDelete(childIds);
        }
        await assetsStore.delete(assetId);
        await contentStore.delete(assetId);
    });

    return childIds;
}
